import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import {
  TextInputField,
  CheckboxItemList,
  CheckboxInputField,
  SelectInputField,
  SwitchInputField,
  RichTextField,
  charCountLabel,
} from '../../../components/input';
import * as Abn from '../../../lib/abn';
import SectionTitle from '../../../components/SectionTitle.js';
import update from 'immutability-helper';
import Alert from '@material-ui/lab/Alert';
import Button from '../../../components/Button.js';
import Loader from '../../../components/Loader.js';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ImageDropZone from './ImageDropZone';
import { ProviderAPI } from '../../../api';
import { TaxonomyStore } from '../../../store';
import { taxonomyTreeToIdArray, taxonomyListToIdArray } from '../../../lib/util.js';
import equal from 'fast-deep-equal/es6/react';
import { dataDiff } from '../../../lib/util.js';
import { uploadProviderImage } from '../../../api/s3';

const orgTypeOptions = ['sole trader', 'digital platform', 'agency'];

const boolFields = [
  'ndis_registered',
  'ndis_early_childhood_registered',
  'active',
];

const multiSelectFields = [
  'services',
  'service_locations',
  'access_methods',
  'age_groups',
  'specialisations'
];

const useStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    padding: 0,
  },
  title: {
    marginBottom: '1rem',
  },
  controls: {
    padding: '1rem 0',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginLeft: '1rem',
    width: '8rem',
    height: '3rem',
  },
});

const EditProfile = ({providerUuid}) => {
  const classes = useStyles();
  const [busy, setBusy] = React.useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertState, setAlterState] = React.useState(null);
  const [alertCaption, setAlterCaption] = React.useState('');
  const [initial, setInitial] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [imageFile, setImageFile] = React.useState(null);
  const [abnWarning, setAbnWarning] = React.useState();
  const locationTaxonomy = useSelector(state => state.taxonomy.locations);
  const serviceTaxonomy = useSelector(state => state.taxonomy.services);
  const accessMethodTaxonomy = useSelector(state => state.taxonomy.accessMethods);
  const ageGroupTaxonomy = useSelector(state => state.taxonomy.ageGroups);
  const specialisationTaxonomy = useSelector(state => state.taxonomy.specialisations);
  const dispatch = useDispatch();

  const validateAbn = (abn) => {
    setAbnWarning((abn === '' || abn === undefined) ? undefined : Abn.validateAbn(abn));
  };

  // Convert all list and tree attributes to ID lists
  const initialiseData = (initialData) => {
    const initialised = {};
    Object.keys(initialData).forEach(k => {
      if (!Array.isArray(initialData[k])) {
        initialised[k] = initialData[k];
      }
    });
    initialised['services'] = taxonomyTreeToIdArray(initialData.services);
    initialised['service_locations'] = taxonomyTreeToIdArray(initialData.service_locations);
    initialised['access_methods'] = taxonomyListToIdArray(initialData.access_methods);
    initialised['age_groups'] = taxonomyListToIdArray(initialData.age_groups);
    initialised['specialisations'] = taxonomyListToIdArray(initialData.specialisations);
    setInitial(initialised);
    setData(initialised);
    validateAbn(initialised.abn);
  };

  const getData = async () => {
    try {
      dispatch(TaxonomyStore.refreshTaxonomy('locations'));
      dispatch(TaxonomyStore.refreshTaxonomy('services'));
      dispatch(TaxonomyStore.refreshTaxonomy('accessMethods'));
      dispatch(TaxonomyStore.refreshTaxonomy('ageGroups'));
      dispatch(TaxonomyStore.refreshTaxonomy('specialisations'));
      const profile = await ProviderAPI.getProfile(providerUuid);
      initialiseData(profile.payload);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const triggerAlert = (caption, variant) => {
    setAlterCaption(caption);
    setAlterState(variant);
    setAlertOpen(true);
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const handleChange = (value, field) => {
    let updatedData;
    if (multiSelectFields.includes(field)) {
      const intValue = parseInt(value);
      const currentArr = data[field];
      let newArr;
      if (currentArr.includes(intValue)) {
        newArr = currentArr.filter(item => item !== intValue);
      } else {
        newArr = currentArr.filter(item => item); // must deep copy
        newArr.push(intValue);
      }
      updatedData = update(data, {[field]: {$set: newArr}});
    } else if (boolFields.includes(field)) {
      const current = data[field];
      updatedData = update(data, {[field]: {$set: !current}});
    } else {
      //updatedData = update(data, {[field]: {$set: value}});
      updatedData = {...data, [field]: value};
    }
    setData(updatedData);
  };

  const save = async () => {
    const dataChanged = !equal(initial, data);
    if (!dataChanged && !imageFile) {
      triggerAlert('No data has changed.', 'info');
      return null;
    }
    try {
      setBusy(true);
      if (dataChanged) {
        const patchData = dataDiff(initial, data);  // Only submit what has changed
        await ProviderAPI.updateProfile(providerUuid, patchData);
        setInitial(data);
      }
      if (imageFile) {
        await uploadProviderImage(providerUuid, imageFile);
        setImageFile();
      }
      triggerAlert('Provider profile successfully updated.', 'success');
      setBusy(false);
    } catch (err) {
      triggerAlert('Update failed. Check you have entered all information correctly.', 'warning');
      setBusy(false);
    }
  };

  React.useEffect(() => {
    getData();
  }, []); // eslint-disable-line

  if (data === null) {
    return (<Loader />);
  }

  const switchLabel = (data.active) ? 'Profile is publicly visible' : 'Profile is not publicly visible';

  return (
    <div className={classes.root}>
      <ExpansionPanel defaultExpanded>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-description"
          id="panel-description"
        >
          <SectionTitle title="Description" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <TextInputField
            {...charCountLabel('Trading Name', data.name, 255)}
            error={data && data.name && data.name.length > 255}
            value={data.name}
            onChange={e => handleChange(e.target.value, 'name')}
          />
          <SwitchInputField
            label="Provider Profile Visibility on Clickability"
            switchLabel={switchLabel}
            value={data.active}
            onChange={e => handleChange(e.target.value, 'active')}
            color="primary"
            name="active"
          />
          <TextInputField
            label="ABN"
            error={abnWarning !== undefined}
            helperText={abnWarning}
            value={data.abn}
            onChange={e => handleChange(e.target.value, 'abn')}
            onBlur={() => {
              validateAbn(data.abn);
              handleChange(Abn.formatAbn(data.abn), 'abn');
            }}
          />
          <TextInputField
            {...charCountLabel('Registered Name', data.entity_name, 255)}
            error={data && data.entity_name && data.entity_name.length > 255}
            value={data.entity_name}
            onChange={e => handleChange(e.target.value, 'entity_name')}
          />
          <SelectInputField
            label="Organisation type"
            value={data.organisation_type}
            options={orgTypeOptions}
            onChange={e => handleChange(e.target.value, 'organisation_type')}
          />
          <TextInputField
            label="NDIS Provider Number"
            value={data.provider_no}
            onChange={e => handleChange(e.target.value, 'provider_no')}
          />
          <TextInputField
            {...charCountLabel('Short Description', data.short_description, 255)}
            value={data.short_description}
            onChange={e => handleChange(e.target.value, 'short_description')}
          />
          <RichTextField
            inputValue={data.full_description}
            allowEdit={true}
            label="Full Description"
            onChange={value => handleChange(value, 'full_description')}
          />
          <TextInputField
            {...charCountLabel('Good to Know', data.good_to_know, 255)}
            value={data.good_to_know}
            multiline
            rows={3}
            onChange={e => handleChange(e.target.value, 'good_to_know')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-image"
          id="panel-image"
        >
          <SectionTitle title="Profile Image" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <ImageDropZone
            instructions={['We recommend 1280x720 pixel JPG or PNG images for profile images.']}
            imageUrl={data.image_path}
            setImageFile={setImageFile}
            clearImageUrl={() => handleChange(null, 'image_path')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-contact"
          id="panel-contact"
        >
          <SectionTitle title="Contact information" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <TextInputField
            label="Phone"
            value={data.phone}
            onChange={e => handleChange(e.target.value, 'phone')}
          />
          <TextInputField
            label="Email"
            value={data.email}
            onChange={e => handleChange(e.target.value, 'email')}
          />
          <TextInputField
            label="Website"
            value={data.website}
            onChange={e => handleChange(e.target.value, 'website')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-ndis"
          id="panel-ndis"
        >
          <SectionTitle title="NDIS Registration" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CheckboxInputField
            label="NDIS Registered"
            value={data.ndis_registered}
            onChange={e => handleChange(e.target.value, 'ndis_registered')}
          />
          <CheckboxInputField
            label="NDIS Early Childhood Registered"
            value={data.ndis_early_childhood_registered}
            onChange={e => handleChange(e.target.value, 'ndis_early_childhood_registered')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-services"
          id="panel-services"
        >
          <SectionTitle title="Services" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          {serviceTaxonomy.map(item => {
            return (
              <CheckboxItemList
                items={item.children}
                checkedItems={data.services}
                header={item.name}
                key={item.id}
                onChange={e => handleChange(e.target.value, 'services')}
              />
            );
          })}
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-locations"
          id="panel-locations"
        >
          <SectionTitle title="Service Locations" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          {locationTaxonomy.map(item => {
            return (
              <CheckboxItemList
                items={item.children}
                checkedItems={data.service_locations}
                header={item.name}
                key={item.id}
                onChange={e => handleChange(e.target.value, 'service_locations')}
              />
            );
          })}
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-access"
          id="panel-access"
        >
          <SectionTitle title="Access Methods" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CheckboxItemList
            items={accessMethodTaxonomy}
            checkedItems={data.access_methods}
            header={null}
            onChange={e => handleChange(e.target.value, 'access_methods')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-age"
          id="panel-age"
        >
          <SectionTitle title="Age Groups" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CheckboxItemList
            items={ageGroupTaxonomy}
            checkedItems={data.age_groups}
            header={null}
            onChange={e => handleChange(e.target.value, 'age_groups')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-specialisations"
          id="panel-specialisations"
        >
          <SectionTitle title="Specialisations" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CheckboxItemList
            items={specialisationTaxonomy}
            checkedItems={data.specialisations}
            header={null}
            onChange={e => handleChange(e.target.value, 'specialisations')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel>
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-social"
          id="panel-social"
        >
          <SectionTitle title="Social Network Links" />
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <TextInputField
            label="Facebook link"
            value={data.facebook_url}
            onChange={e => handleChange(e.target.value, 'facebook_url')}
          />
          <TextInputField
            label="Twitter link"
            value={data.twitter_url}
            onChange={e => handleChange(e.target.value, 'twitter_url')}
          />
          <TextInputField
            label="Instagram link"
            value={data.instagram_url}
            onChange={e => handleChange(e.target.value, 'instagram_url')}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <div className={classes.controls}>
        {alertOpen ? (
          <Alert severity={alertState} onClose={closeAlert}>
            {alertCaption}
          </Alert>
        ) : null}
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          label="Save"
          busy={busy}
          className={classes.button}
          pointer="none"
        />
      </div>

    </div>
  );
};

EditProfile.propTypes = {
  providerUuid: PropTypes.string.isRequired,
};

export default EditProfile;
